import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { addDays } from 'date-fns';
import { supabase } from '@/lib/supabase';
import type {
  CampaignStatus,
  CreateVendorActivityInput,
  CrmSourceMappingQueueItem,
  CrmAccessRole,
  GlobalCrmSearchResult,
  ProfileOption,
  Vendor,
  VendorActivityRevision,
  VendorCampaign,
  VendorContact,
  VendorDirectoryFilters,
  VendorDirectoryRow,
  VendorFollowUp,
  VendorInsightLink,
  VendorInsightState,
  VendorTimelineItem,
  VendorType,
} from './types';

type DbRow = Record<string, unknown>;

const stringValue = (row: DbRow, key: string, fallback = '') =>
  typeof row[key] === 'string' ? row[key] as string : fallback;
const nullableString = (row: DbRow, key: string) => {
  const value = row[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
};
const numberValue = (row: DbRow, key: string) => {
  const value = row[key];
  return typeof value === 'number' ? value : typeof value === 'string' && Number.isFinite(Number(value)) ? Number(value) : 0;
};
const booleanValue = (row: DbRow, key: string) => row[key] === true;

function mapVendor(row: DbRow): Vendor {
  return {
    id: stringValue(row, 'id'),
    displayName: stringValue(row, 'display_name', 'Unnamed vendor'),
    normalizedName: stringValue(row, 'normalized_name'),
    vendorType: stringValue(row, 'vendor_type', 'other') as Vendor['vendorType'],
    status: stringValue(row, 'status', 'onboarding') as Vendor['status'],
    relationshipOwnerId: nullableString(row, 'relationship_owner_id'),
    relationshipOwnerEmail: nullableString(row, 'relationship_owner_email'),
    website: nullableString(row, 'website'),
    summary: nullableString(row, 'summary'),
    createdAt: stringValue(row, 'created_at'),
    updatedAt: stringValue(row, 'updated_at'),
  };
}

function mapDirectoryVendor(row: DbRow): VendorDirectoryRow {
  return {
    ...mapVendor(row),
    activeCampaignCount: numberValue(row, 'active_campaign_count'),
    lastActivityAt: nullableString(row, 'last_activity_at'),
    lastActivitySummary: nullableString(row, 'last_activity_summary'),
    nextFollowUpAt: nullableString(row, 'next_follow_up_at'),
    nextFollowUpTitle: nullableString(row, 'next_follow_up_title'),
    sourceCount: numberValue(row, 'source_count'),
    sourceState: stringValue(row, 'source_state', 'needs_mapping') as VendorDirectoryRow['sourceState'],
  };
}

function mapCampaign(row: DbRow): VendorCampaign {
  return {
    id: stringValue(row, 'id'),
    vendorId: stringValue(row, 'vendor_id'),
    displayName: stringValue(row, 'display_name', 'Unnamed campaign'),
    status: stringValue(row, 'status', 'planned') as VendorCampaign['status'],
    relationshipStatus: stringValue(row, 'relationship_status', 'active') as VendorCampaign['relationshipStatus'],
    vendorLabel: nullableString(row, 'vendor_label'),
    isPrimary: booleanValue(row, 'is_primary'),
    description: nullableString(row, 'description'),
    startsOn: nullableString(row, 'starts_on'),
    endsOn: nullableString(row, 'ends_on'),
    activityCount: numberValue(row, 'activity_count'),
    lastActivityAt: nullableString(row, 'last_activity_at'),
    sourceCount: numberValue(row, 'source_count'),
  };
}

function mapContact(row: DbRow): VendorContact {
  return {
    id: stringValue(row, 'id'),
    vendorId: stringValue(row, 'vendor_id'),
    displayName: stringValue(row, 'display_name', 'Unnamed contact'),
    firstName: nullableString(row, 'first_name'),
    lastName: nullableString(row, 'last_name'),
    jobTitle: nullableString(row, 'job_title'),
    email: nullableString(row, 'email'),
    phone: nullableString(row, 'phone'),
    preferredContactMethod: stringValue(row, 'preferred_contact_method', 'email') as VendorContact['preferredContactMethod'],
    isPrimary: booleanValue(row, 'is_primary'),
    status: stringValue(row, 'status', 'active') as VendorContact['status'],
    notes: nullableString(row, 'notes'),
    createdAt: stringValue(row, 'created_at'),
    updatedAt: stringValue(row, 'updated_at'),
  };
}

function mapFollowUp(row: DbRow): VendorFollowUp {
  const vendor = row.crm_vendors;
  return {
    id: stringValue(row, 'id'),
    vendorId: stringValue(row, 'vendor_id'),
    campaignId: nullableString(row, 'campaign_id'),
    contactId: nullableString(row, 'contact_id'),
    originatingActivityId: nullableString(row, 'originating_activity_id'),
    title: stringValue(row, 'title'),
    assigneeId: stringValue(row, 'assignee_id'),
    dueAt: stringValue(row, 'due_at'),
    status: stringValue(row, 'status', 'open') as VendorFollowUp['status'],
    createdBy: nullableString(row, 'created_by'),
    completedAt: nullableString(row, 'completed_at'),
    vendorName: vendor && typeof vendor === 'object' && typeof (vendor as DbRow).display_name === 'string'
      ? (vendor as DbRow).display_name as string
      : undefined,
  };
}

function mapTimeline(row: DbRow): VendorTimelineItem {
  const rawLinks = Array.isArray(row.links) ? row.links : [];
  return {
    id: stringValue(row, 'id'),
    vendorId: stringValue(row, 'vendor_id'),
    campaignId: nullableString(row, 'campaign_id'),
    campaignName: nullableString(row, 'campaign_name'),
    contactId: nullableString(row, 'contact_id'),
    contactName: nullableString(row, 'contact_name'),
    kind: stringValue(row, 'kind', 'note') as VendorTimelineItem['kind'],
    subject: nullableString(row, 'subject'),
    body: stringValue(row, 'body'),
    occurredAt: stringValue(row, 'occurred_at'),
    authorId: nullableString(row, 'author_id'),
    authorLabel: stringValue(row, 'author_label', 'System'),
    sourceSystem: stringValue(row, 'source_system', 'manual'),
    topic: stringValue(row, 'topic', 'general') as VendorTimelineItem['topic'],
    purpose: stringValue(row, 'purpose', 'note') as VendorTimelineItem['purpose'],
    revisionNumber: numberValue(row, 'revision_number'),
    isVoid: booleanValue(row, 'is_void'),
    voidReason: nullableString(row, 'void_reason'),
    createdAt: stringValue(row, 'created_at'),
    updatedAt: stringValue(row, 'updated_at'),
    links: rawLinks.flatMap((value) => {
      if (!value || typeof value !== 'object') return [];
      const link = value as DbRow;
      return [{
        id: stringValue(link, 'id'),
        label: stringValue(link, 'label', 'Open link'),
        url: nullableString(link, 'url'),
        sourceRecordId: nullableString(link, 'source_record_id'),
      }];
    }),
  };
}

export async function getCrmAccess(): Promise<CrmAccessRole | null> {
  const { data, error } = await supabase.rpc('crm_my_access');
  if (error) throw new Error(`Could not load CRM access: ${error.message}`);
  return data === 'admin' ? 'admin' : null;
}

export async function getProfileOptions(): Promise<ProfileOption[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id,email')
    .eq('is_active', true)
    .eq('role', 'admin_level_3')
    .order('email');
  if (error) throw new Error(`Could not load team members: ${error.message}`);
  return ((data ?? []) as DbRow[]).map((row) => ({ id: stringValue(row, 'id'), email: stringValue(row, 'email') }));
}

// Relationship ownership does not imply permission to execute CRM tasks.
export async function getRelationshipOwnerOptions(): Promise<ProfileOption[]> {
  const { data, error } = await supabase.from('profiles').select('id,email')
    .eq('is_active', true).or('is_account_manager.eq.true,role.eq.admin_level_3').order('email');
  if (error) throw new Error(`Could not load account managers: ${error.message}`);
  return ((data ?? []) as DbRow[]).map((row) => ({ id: stringValue(row, 'id'), email: stringValue(row, 'email') }));
}

export async function getVendorDirectory(
  filters: VendorDirectoryFilters,
  page: number,
  pageSize = 50,
): Promise<{ items: VendorDirectoryRow[]; count: number }> {
  let query = supabase
    .from('crm_vendor_directory')
    .select('*', { count: 'exact' })
    .order('next_follow_up_at', { ascending: true, nullsFirst: false })
    .order('display_name', { ascending: true })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (filters.search.trim()) query = query.ilike('display_name', `%${filters.search.trim()}%`);
  if (filters.status === 'all') query = query.neq('status', 'archived');
  else query = query.eq('status', filters.status);
  if (filters.ownerId !== 'all') query = query.eq('relationship_owner_id', filters.ownerId);
  if (filters.overdueOnly) query = query.lt('next_follow_up_at', new Date().toISOString());
  if (filters.sourceState !== 'all') query = query.eq('source_state', filters.sourceState);

  const { data, error, count } = await query;
  if (error) throw new Error(`Could not load vendors: ${error.message}`);
  return { items: ((data ?? []) as DbRow[]).map(mapDirectoryVendor), count: count ?? 0 };
}

export async function getMyFollowUps(userId: string): Promise<VendorFollowUp[]> {
  const tomorrow = fromZonedTime(`${formatInTimeZone(addDays(new Date(), 1), 'America/Denver', 'yyyy-MM-dd')}T23:59:59`, 'America/Denver');
  const { data, error } = await supabase
    .from('crm_follow_ups')
    .select('*,crm_vendors(display_name)')
    .eq('assignee_id', userId)
    .eq('status', 'open')
    .lte('due_at', tomorrow.toISOString())
    .order('due_at')
    .limit(8);
  if (error) throw new Error(`Could not load your follow-ups: ${error.message}`);
  return ((data ?? []) as DbRow[]).map(mapFollowUp);
}

export async function createVendor(input: {
  displayName: string;
  vendorType: VendorType;
  relationshipOwnerId: string | null;
  website: string;
  summary: string;
  createdBy: string;
}): Promise<Vendor> {
  const { data, error } = await supabase
    .from('crm_vendors')
    .insert({
      display_name: input.displayName.trim(),
      vendor_type: input.vendorType,
      status: 'onboarding',
      relationship_owner_id: input.relationshipOwnerId,
      website: input.website.trim() || null,
      summary: input.summary.trim() || null,
      created_by: input.createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(`Could not add vendor: ${error.message}`);
  return mapVendor(data as DbRow);
}

export async function getVendor(vendorId: string): Promise<VendorDirectoryRow> {
  const { data, error } = await supabase
    .from('crm_vendor_directory')
    .select('*')
    .eq('id', vendorId)
    .single();
  if (error) throw new Error(`Could not load vendor: ${error.message}`);
  return mapDirectoryVendor(data as DbRow);
}

export async function updateVendor(vendorId: string, patch: {
  status?: Vendor['status'];
  relationshipOwnerId?: string | null;
  website?: string | null;
  summary?: string | null;
}): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.status !== undefined) {
    payload.status = patch.status;
    payload.archived_at = patch.status === 'archived' ? new Date().toISOString() : null;
  }
  if (patch.relationshipOwnerId !== undefined) payload.relationship_owner_id = patch.relationshipOwnerId;
  if (patch.website !== undefined) payload.website = patch.website;
  if (patch.summary !== undefined) payload.summary = patch.summary;
  const { error } = await supabase.from('crm_vendors').update(payload).eq('id', vendorId);
  if (error) throw new Error(`Could not update vendor: ${error.message}`);
}

export async function getVendorContacts(vendorId: string): Promise<VendorContact[]> {
  const { data, error } = await supabase
    .from('crm_contacts')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('is_primary', { ascending: false })
    .order('display_name');
  if (error) throw new Error(`Could not load contacts: ${error.message}`);
  return ((data ?? []) as DbRow[]).map(mapContact);
}

export async function createVendorContact(input: {
  vendorId: string;
  displayName: string;
  jobTitle: string;
  email: string;
  phone: string;
  preferredContactMethod: VendorContact['preferredContactMethod'];
  isPrimary: boolean;
  notes: string;
  createdBy: string;
}): Promise<VendorContact> {
  const { data, error } = await supabase.rpc('create_crm_contact', {
    p_payload: {
      vendor_id: input.vendorId,
      display_name: input.displayName.trim(),
      job_title: input.jobTitle.trim() || null,
      email: input.email.trim() || null,
      phone: input.phone.trim() || null,
      preferred_contact_method: input.preferredContactMethod,
      is_primary: input.isPrimary,
      notes: input.notes.trim() || null,
      created_by: input.createdBy,
    },
  });
  if (error) throw new Error(error.code === '23505' ? 'That email or primary contact is already in use for this vendor.' : `Could not add contact: ${error.message}`);
  return mapContact(data as DbRow);
}

export async function updateVendorContact(contactId: string, patch: Partial<{
  status: VendorContact['status'];
  isPrimary: boolean;
  notes: string | null;
}>): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.isPrimary !== undefined) payload.is_primary = patch.isPrimary;
  if (patch.notes !== undefined) payload.notes = patch.notes;
  const { error } = await supabase.from('crm_contacts').update(payload).eq('id', contactId);
  if (error) throw new Error(`Could not update contact: ${error.message}`);
}

export async function setVendorPrimaryContact(contactId: string): Promise<void> {
  const { error } = await supabase.rpc('set_crm_primary_contact', { p_contact_id: contactId });
  if (error) throw new Error(`Could not set primary contact: ${error.message}`);
}

export async function getVendorCampaigns(vendorId: string): Promise<VendorCampaign[]> {
  const { data, error } = await supabase
    .from('crm_vendor_campaign_overview')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('is_primary', { ascending: false })
    .order('display_name');
  if (error) throw new Error(`Could not load campaigns: ${error.message}`);
  return ((data ?? []) as DbRow[]).map(mapCampaign);
}

export async function createVendorCampaign(vendorId: string, name: string, status: CampaignStatus): Promise<void> {
  const { error } = await supabase.rpc('create_crm_campaign_for_vendor', {
    p_vendor_id: vendorId,
    p_name: name.trim(),
    p_status: status,
  });
  if (error) throw new Error(`Could not add campaign: ${error.message}`);
}

export interface TimelineFilters {
  topic?: string;
  kind: VendorTimelineItem['kind'] | 'all';
  campaignId: string | 'all';
  contactId: string | 'all';
  authorId: string | 'all';
}

export interface VendorTimelinePage {
  items: VendorTimelineItem[];
  nextPage: number | null;
}

export async function getVendorTimeline(
  vendorId: string,
  filters: TimelineFilters,
  scopedCampaignId?: string | null,
  page = 0,
  pageSize = 50,
): Promise<VendorTimelinePage> {
  let query = supabase
    .from('crm_vendor_timeline')
    .select('*', { count: 'exact' })
    .eq('vendor_id', vendorId)
    .order('occurred_at', { ascending: false })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);
  if (scopedCampaignId) query = query.eq('campaign_id', scopedCampaignId);
  else if (filters.campaignId !== 'all') query = query.eq('campaign_id', filters.campaignId);
  if (filters.kind !== 'all') query = query.eq('kind', filters.kind);
  if (filters.topic) query = query.eq('topic', filters.topic);
  if (filters.contactId !== 'all') query = query.eq('contact_id', filters.contactId);
  if (filters.authorId !== 'all') query = query.eq('author_id', filters.authorId);
  const { data, error, count } = await query;
  if (error) throw new Error(`Could not load activity: ${error.message}`);
  const items = ((data ?? []) as DbRow[]).map(mapTimeline);
  return {
    items,
    nextPage: (page + 1) * pageSize < (count ?? 0) ? page + 1 : null,
  };
}

export async function getActivityRevisions(activityId: string): Promise<VendorActivityRevision[]> {
  const { data, error } = await supabase
    .from('crm_activity_revisions')
    .select('*')
    .eq('activity_id', activityId)
    .order('revision_number', { ascending: false });
  if (error) throw new Error(`Could not load revision history: ${error.message}`);
  return ((data ?? []) as DbRow[]).map((row) => ({
    id: stringValue(row, 'id'),
    activityId: stringValue(row, 'activity_id'),
    revisionNumber: numberValue(row, 'revision_number'),
    previousRecord: row.previous_record && typeof row.previous_record === 'object' ? row.previous_record as Record<string, unknown> : {},
    revisedBy: nullableString(row, 'revised_by'),
    reason: nullableString(row, 'reason'),
    createdAt: stringValue(row, 'created_at'),
  }));
}

export async function createVendorActivity(input: CreateVendorActivityInput): Promise<void> {
  const { error } = await supabase.rpc('crm_capture', {
    p_payload: {
      request_id: input.requestId ?? crypto.randomUUID(),
      topic: input.topic ?? 'general', purpose: input.purpose ?? 'note',
      report_start: input.reportStart || null, report_end: input.reportEnd || null,
      vendor_id: input.vendorId,
      campaign_id: input.campaignId || null,
      contact_id: input.contactId || null,
      kind: input.kind,
      subject: input.subject?.trim() || null,
      body: input.body.trim(),
      occurred_at: input.occurredAt,
      external_url: input.externalUrl?.trim() || null,
      follow_up: input.followUp ? {
        title: input.followUp.title.trim(),
        assignee_id: input.followUp.assigneeId,
        due_at: input.followUp.dueAt,
      } : null,
    },
  });
  if (error) throw new Error(`Could not save activity: ${error.message}`);
}

export async function reviseVendorActivity(
  activityId: string,
  expectedUpdatedAt: string,
  patch: { subject: string | null; body: string; reason: string },
): Promise<void> {
  const { error } = await supabase.rpc('revise_crm_activity', {
    p_activity_id: activityId,
    p_expected_updated_at: expectedUpdatedAt,
    p_patch: patch,
  });
  if (error) {
    throw new Error(error.code === '40001'
      ? 'This activity changed while you were editing it. Reload the timeline and try again.'
      : `Could not revise activity: ${error.message}`);
  }
}

export async function voidVendorActivity(activityId: string, expectedUpdatedAt: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('void_crm_activity', {
    p_activity_id: activityId,
    p_expected_updated_at: expectedUpdatedAt,
    p_reason: reason.trim(),
  });
  if (error) {
    throw new Error(error.code === '40001'
      ? 'This activity changed before it could be voided. Reload the timeline and try again.'
      : `Could not void activity: ${error.message}`);
  }
}

export async function getVendorFollowUps(vendorId: string): Promise<VendorFollowUp[]> {
  const { data, error } = await supabase
    .from('crm_follow_ups')
    .select('*')
    .eq('vendor_id', vendorId)
    .eq('status', 'open')
    .order('due_at');
  if (error) throw new Error(`Could not load follow-ups: ${error.message}`);
  return ((data ?? []) as DbRow[]).map(mapFollowUp);
}

export async function completeVendorFollowUp(followUpId: string): Promise<void> {
  const { error } = await supabase.rpc('crm_update_task', { p_id: followUpId, p_patch: { status: 'completed' } });
  if (error) throw new Error(`Could not complete follow-up: ${error.message}`);
}

export async function getVendorInsights(vendorId: string): Promise<VendorInsightLink[]> {
  const { data: links, error } = await supabase
    .from('crm_vendor_source_links')
    .select('source_record_id,crm_source_records(source_system,object_type,source_key,external_id,display_label,last_seen_at)')
    .eq('vendor_id', vendorId);
  if (error) throw new Error(`Could not load source connections: ${error.message}`);

  const sourceRows = ((links ?? []) as DbRow[]).flatMap((link) => {
    const source = link.crm_source_records;
    if (Array.isArray(source)) {
      return source.filter((value): value is DbRow => Boolean(value) && typeof value === 'object');
    }
    return source && typeof source === 'object' ? [source as DbRow] : [];
  });
  const ringba = sourceRows.find((row) => stringValue(row, 'source_system') === 'ringba' && stringValue(row, 'object_type') === 'publisher');

  let callCount = 0;
  let lastCallAt: string | null = null;
  let adLibraryCount = 0;
  let callState: VendorInsightState = ringba ? 'restricted' : 'needs_mapping';
  let adLibraryState: VendorInsightState = ringba ? 'restricted' : 'needs_mapping';
  if (ringba) {
    const snapshotResult = await supabase.rpc('crm_vendor_insight_snapshot', { p_vendor_id: vendorId });
    if (!snapshotResult.error && snapshotResult.data && typeof snapshotResult.data === 'object') {
      const snapshot = snapshotResult.data as DbRow;
      const readState = (key: string): VendorInsightState => {
        const value = stringValue(snapshot, key);
        return value === 'connected' || value === 'needs_mapping' || value === 'stale' || value === 'restricted' || value === 'unavailable'
          ? value
          : 'restricted';
      };
      callState = readState('call_state');
      adLibraryState = readState('ad_library_state');
      callCount = numberValue(snapshot, 'call_count');
      lastCallAt = nullableString(snapshot, 'last_call_at');
      adLibraryCount = numberValue(snapshot, 'ad_library_count');
    }
  }
  const publisherId = ringba ? stringValue(ringba, 'source_key') : '';
  const resolvedAdLibraryState: VendorInsightState = adLibraryState === 'connected' && adLibraryCount === 0
    ? 'unavailable'
    : adLibraryState;

  return [
    {
      id: 'ringba', label: 'Ringba', state: ringba ? 'connected' : 'needs_mapping',
      summary: ringba ? 'Verified publisher identity' : 'Publisher identity is not mapped',
      detail: ringba ? 'Canonical publisher and campaign relationships are connected through verified Ringba IDs.' : 'Use the source mapping queue before attributing calls or campaigns.',
      updatedAt: ringba ? nullableString(ringba, 'last_seen_at') : null,
      href: ringba ? `/ringba/performance?publisher=${encodeURIComponent(publisherId)}` : null,
    },
    {
      id: 'call-insights', label: 'Call insights', state: callState,
      summary: callState === 'needs_mapping' ? 'Waiting for a verified Ringba publisher' : callState === 'restricted' ? 'Call summary is not authorized' : callState === 'unavailable' ? 'Call source is unavailable' : `${callCount.toLocaleString()} ${callCount === 1 ? 'call' : 'calls'}${lastCallAt ? ` · latest ${new Date(lastCallAt).toLocaleDateString()}` : ''}`,
      detail: callState === 'connected' ? 'Open the governed Ringba call surface for performance, blocking, and attestation signals.' : 'The CRM did not query legacy call rows in the browser or substitute zero when the governed summary was unavailable.',
      updatedAt: lastCallAt,
      href: ringba && callState === 'connected' ? `/ringba/performance?publisher=${encodeURIComponent(publisherId)}` : null,
    },
    {
      id: 'domains', label: 'Domains', state: sourceRows.some((row) => stringValue(row, 'source_system') === 'domains') ? 'connected' : 'needs_mapping',
      summary: 'Verified domain aliases only',
      detail: 'Discovery names are never fuzzy-matched. Confirm a vendor or campaign identity before attribution.',
      updatedAt: null,
      href: '/domain-directory',
    },
    {
      id: 'ad-libraries', label: 'Ad libraries', state: resolvedAdLibraryState,
      summary: resolvedAdLibraryState === 'needs_mapping' ? 'Waiting for a verified Ringba publisher' : resolvedAdLibraryState === 'restricted' ? 'Ad library summary is not authorized' : adLibraryCount > 0 ? `${adLibraryCount} monitored ${adLibraryCount === 1 ? 'library' : 'libraries'}` : 'No monitored library connected',
      detail: resolvedAdLibraryState === 'connected' ? 'Google and Meta monitoring is linked through the verified Ringba publisher relationship.' : 'The CRM did not query legacy ad-library rows in the browser or report a false zero.',
      updatedAt: null,
      href: adLibraryCount > 0 && ringba ? `/ad-compliance/libraries?publisher=${encodeURIComponent(publisherId)}` : null,
    },
    {
      id: 'integrishield', label: 'IntegriShield audits', state: 'needs_mapping',
      summary: 'Shared audit mapping not configured',
      detail: 'The existing analyst workspace remains available; local-only notes are not imported automatically.',
      updatedAt: null,
      href: '/integrishield',
    },
    {
      id: 'meera', label: 'Meera AI', state: 'restricted',
      summary: 'Access path requires security hardening',
      detail: 'CRM does not query the legacy RLS-disabled tables from the browser.',
      updatedAt: null,
      href: null,
    },
    {
      id: 'ipqs', label: 'Jotform and IPQS', state: 'needs_mapping',
      summary: 'Submission identity is not mapped',
      detail: 'Onboarding and IP risk results appear only after a stable vendor or campaign key is verified.',
      updatedAt: null,
      href: '/ipqs/submissions',
    },
    {
      id: 'clickup', label: 'ClickUp', state: 'unavailable',
      summary: 'Add a task URL to an activity',
      detail: 'V1 stores safe manual links; task creation and bidirectional sync are intentionally deferred.',
      updatedAt: null,
      href: null,
    },
    {
      id: 'trustedform', label: 'TrustedForm', state: 'unavailable',
      summary: 'Adapter reserved',
      detail: 'TrustedForm data will remain unattributed until a stable source key is available.',
      updatedAt: null,
      href: null,
    },
    {
      id: 'invoicing', label: 'Invoicing', state: 'unavailable',
      summary: 'Not configured',
      detail: 'The CRM reserves the integration slot without calculating or presenting financial totals.',
      updatedAt: null,
      href: null,
    },
  ];
}

export async function getCrmSourceMappingQueue(limit = 50): Promise<CrmSourceMappingQueueItem[]> {
  const { data, error } = await supabase
    .from('crm_source_mapping_queue')
    .select('*')
    .order('last_seen_at', { ascending: false })
    .limit(Math.max(1, Math.min(limit, 100)));
  if (error) throw new Error(`Could not load the source mapping queue: ${error.message}`);
  return ((data ?? []) as DbRow[]).map((row) => ({
    sourceRecordId: stringValue(row, 'source_record_id'),
    sourceSystem: stringValue(row, 'source_system'),
    objectType: stringValue(row, 'object_type'),
    sourceKey: stringValue(row, 'source_key'),
    externalId: nullableString(row, 'external_id'),
    displayLabel: stringValue(row, 'display_label', 'Unnamed source record'),
    lastSeenAt: stringValue(row, 'last_seen_at'),
    mappedCampaignId: nullableString(row, 'mapped_campaign_id'),
    mappingState: stringValue(row, 'mapping_state', 'source_unmapped') as CrmSourceMappingQueueItem['mappingState'],
  }));
}

export async function resolveCrmSourceMapping(input: {
  sourceRecordId: string;
  entityType: 'vendor' | 'campaign';
  entityId: string;
  matchMethod: 'exact_confirmed' | 'manual';
}): Promise<void> {
  const { error } = await supabase.rpc('resolve_crm_source_mapping', {
    p_source_record_id: input.sourceRecordId,
    p_entity_type: input.entityType,
    p_entity_id: input.entityId,
    p_match_method: input.matchMethod,
  });
  if (error) throw new Error(`Could not approve source mapping: ${error.message}`);
}

export async function linkCrmCampaignToVendor(vendorId: string, campaignId: string): Promise<void> {
  const { error } = await supabase.rpc('link_crm_campaign_to_vendor', {
    p_vendor_id: vendorId,
    p_campaign_id: campaignId,
  });
  if (error) throw new Error(`Could not link campaign to vendor: ${error.message}`);
}

export async function globalCrmSearch(query: string): Promise<GlobalCrmSearchResult[]> {
  const term = query.trim();
  if (term.length < 2) return [];
  const { data, error } = await supabase.rpc('crm_global_search', { p_query: term, p_limit: 5 });
  if (!error) {
    return ((data ?? []) as DbRow[]).map((row) => ({
      entityType: stringValue(row, 'entity_type') as GlobalCrmSearchResult['entityType'],
      entityId: stringValue(row, 'entity_id'),
      label: stringValue(row, 'label'),
      context: stringValue(row, 'context'),
      path: stringValue(row, 'path'),
      sortRank: numberValue(row, 'sort_rank'),
    }));
  }

  // During a staged rollout the CRM migration may not be present yet. Preserve
  // useful domain search without loading entire source tables into the browser.
  if (error.code !== 'PGRST202' && error.code !== '42883') {
    throw new Error(`Could not search ComplianceMagic: ${error.message}`);
  }
  const fallback = await supabase
    .from('approved_domains')
    .select('id,domain_pattern,campaign_id')
    .ilike('domain_pattern', `%${term}%`)
    .limit(6);
  if (fallback.error) throw new Error(`Could not search domains: ${fallback.error.message}`);
  return ((fallback.data ?? []) as DbRow[]).map((row) => ({
    entityType: 'domain',
    entityId: stringValue(row, 'id'),
    label: stringValue(row, 'domain_pattern'),
    context: stringValue(row, 'campaign_id', 'Approved domain'),
    path: '/domain-directory',
    sortRank: 4,
  }));
}
